import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { fetchStudentOfferOwnershipByResourceIds } from "@/lib/student-passes";
import { getToofiNudgeDestinationLabel, getToofiNudgeExplanation } from "@/lib/toofi-nudge-utils";

const hasMissingColumnError = (
  error: { code?: string | null; message?: string | null; details?: string | null; hint?: string | null } | null | undefined,
  column: string,
) => {
  if (!error) return false;
  const haystack = `${error.code ?? ""} ${error.message ?? ""} ${error.details ?? ""} ${error.hint ?? ""}`.toLowerCase();
  return haystack.includes(column.toLowerCase()) && (haystack.includes("column") || haystack.includes("schema cache") || haystack.includes("pgrst"));
};

type BaseNotification = {
  id: string;
  created_at: string;
  title: string;
  link: string | null;
  is_read: boolean;
};

export type NotificationCenterIconKind =
  | "resource"
  | "follower"
  | "submission"
  | "message"
  | "announcement"
  | "ticket"
  | "toofi"
  | "inbox";

export type NotificationCenterAccentTone = "blue" | "emerald" | "amber" | "indigo" | "purple";

export type NotificationCenterTab = "notifications" | "inbox";

export type NotificationItem = BaseNotification & {
  source: "notification";
  user_id: string;
  type: "new_resource" | "new_follower" | "system_alert" | "submission_update" | "access_approved" | "toofi_nudge";
  message: string;
  explanation?: string | null;
  destinationLabel?: string | null;
  badgeLabel?: string | null;
  nudgeEventId?: string | null;
  nudgeEventType?: NudgeEventRow["event_type"] | null;
  nudgeEvidence?: Record<string, unknown> | null;
  snoozedUntil?: string | null;
};

export type InboxMessageItem = BaseNotification & {
  source: "admin_message";
  sender_id: string;
  type: "admin_message";
  message: string;
  priority: "normal" | "urgent";
  recipient_id: string | null;
  recipient_filter: Record<string, string | number> | null;
};

export type NotificationListItemViewModel = {
  id: string;
  iconKind: NotificationCenterIconKind;
  accentTone: NotificationCenterAccentTone;
  title: string;
  body: string;
  explanation?: string | null;
  timestampLabel: string;
  destinationLabel?: string | null;
  badgeLabel?: string | null;
  isUnread: boolean;
  link: string | null;
};

export type InboxListItemViewModel = {
  id: string;
  iconKind: "inbox";
  accentTone: "amber" | "blue";
  title: string;
  body: string;
  timestampLabel: string;
  badgeLabel: string;
  isUnread: boolean;
  link: string | null;
  priority: "normal" | "urgent";
};

type DatabaseNotificationRow = {
  id: string;
  created_at: string;
  user_id: string;
  type: "new_resource" | "new_follower" | "system_alert" | "submission_update" | "access_approved" | "toofi_nudge";
  title: string;
  message: string | null;
  link: string | null;
  is_read: boolean;
};
type AdminMessageRow = {
  id: string;
  sender_id: string;
  recipient_id: string | null;
  recipient_filter: Record<string, string | number> | null;
  title: string;
  body: string;
  link: string | null;
  priority: "normal" | "urgent";
  created_at: string;
};

type AdminMessageReceiptRow = {
  message_id: string;
  user_id: string;
  is_read: boolean;
};

type NudgeEventRow = {
  id: string;
  notification_id: string | null;
  event_type: "review_due" | "plan_reminder" | "streak_break" | "deadline_pressure" | "session_resume" | "celebration";
  dismissed_at: string | null;
  acted_on_at: string | null;
  opened_at?: string | null;
  snoozed_until?: string | null;
  evidence_snapshot?: Record<string, unknown> | null;
};

const formatNotificationTimestamp = (value: string) => {
  const date = new Date(value);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  if (sameDay) {
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }

  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays <= 6) {
    return date.toLocaleDateString([], { weekday: "short" });
  }

  return date.toLocaleDateString([], { month: "short", day: "numeric" });
};

const mapNotificationIconKind = (item: NotificationItem): NotificationCenterIconKind => {
  switch (item.type) {
    case "new_resource":
      return "resource";
    case "new_follower":
      return "follower";
    case "submission_update":
      return "submission";
    case "access_approved":
      return "ticket";
    case "toofi_nudge":
      return "toofi";
    case "system_alert":
      return /access request/i.test(`${item.title} ${item.message}`) ? "message" : "announcement";
    default:
      return "announcement";
  }
};

const mapNotificationAccentTone = (item: NotificationItem): NotificationCenterAccentTone => {
  switch (item.type) {
    case "new_resource":
      return "emerald";
    case "new_follower":
    case "access_approved":
      return "blue";
    case "submission_update":
      return "purple";
    case "toofi_nudge":
      return "indigo";
    case "system_alert":
    default:
      return "amber";
  }
};

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [inboxMessages, setInboxMessages] = useState<InboxMessageItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const loadNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setInboxMessages([]);
      setUnreadCount(0);
      setInboxUnreadCount(0);
      setLoading(false);
      return;
    }

    setLoading(true);

    const [
      { data: dbNotifications, error: dbError },
      { data: adminMessages, error: adminError },
      { data: receipts, error: receiptsError },
    ] = await Promise.all([
      supabase
        .from("notifications" as never)
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20),
      supabase
        .from("admin_messages" as never)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20),
      supabase
        .from("admin_message_receipts" as never)
        .select("message_id,user_id,is_read")
        .eq("user_id", user.id),
    ]);

    if (dbError || adminError || receiptsError) {
      setLoading(false);
      return;
    }

    const receiptMap = new Map(
      ((receipts as AdminMessageReceiptRow[] | null) || []).map((receipt) => [receipt.message_id, receipt.is_read]),
    );

    const standardNotifications: NotificationItem[] = (((dbNotifications as DatabaseNotificationRow[] | null) || []).map((item) => ({
        ...item,
        source: "notification" as const,
        message: item.message || "",
      })))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 20);

    const toofiNotificationIds = standardNotifications
      .filter((item) => item.type === "toofi_nudge")
      .map((item) => item.id);

    let nudgeEventByNotificationId = new Map<string, NudgeEventRow>();
    if (toofiNotificationIds.length) {
      const { data: nudgeEvents, error: nudgeEventsError } = await supabase
        .from("nudge_events")
        .select("*")
        .in("notification_id", toofiNotificationIds);

      let resolvedNudgeEvents = nudgeEvents;
      if (nudgeEventsError && (hasMissingColumnError(nudgeEventsError, "snoozed_until") || hasMissingColumnError(nudgeEventsError, "evidence_snapshot"))) {
        const legacyNudgeEvents = await supabase
          .from("nudge_events")
          .select("*")
          .in("notification_id", toofiNotificationIds);
        if (!legacyNudgeEvents.error) {
          resolvedNudgeEvents = legacyNudgeEvents.data as typeof nudgeEvents;
        }
      }

      nudgeEventByNotificationId = new Map(
        (((resolvedNudgeEvents as NudgeEventRow[] | null) || []).filter((row) => row.notification_id) as Array<
          NudgeEventRow & { notification_id: string }
        >).map((row) => [row.notification_id, row]),
      );
    }

    const passNotificationResourceIds = standardNotifications
      .filter((item) => item.type === "system_alert" && item.link?.startsWith("/resource/"))
      .map((item) => item.link?.split("/resource/")[1] || "")
      .filter(Boolean);

    let passOwnershipByResource: Record<string, Awaited<ReturnType<typeof fetchStudentOfferOwnershipByResourceIds>>[string]> = {};
    if (passNotificationResourceIds.length) {
      try {
        passOwnershipByResource = await fetchStudentOfferOwnershipByResourceIds(user.id, passNotificationResourceIds);
      } catch {
        passOwnershipByResource = {};
      }
    }

    const normalizedNotifications = standardNotifications.map((item) => {
      if (item.type !== "system_alert" || !item.link?.startsWith("/resource/")) {
        return item.type === "toofi_nudge"
          ? {
              ...item,
            explanation: getToofiNudgeExplanation(
                nudgeEventByNotificationId.get(item.id)?.event_type ?? "session_resume",
              ),
              destinationLabel: getToofiNudgeDestinationLabel(item.link),
              badgeLabel: "Toofi",
              nudgeEventId: nudgeEventByNotificationId.get(item.id)?.id ?? null,
              nudgeEventType: nudgeEventByNotificationId.get(item.id)?.event_type ?? null,
              nudgeEvidence: nudgeEventByNotificationId.get(item.id)?.evidence_snapshot ?? null,
              snoozedUntil: nudgeEventByNotificationId.get(item.id)?.snoozed_until ?? null,
            }
          : item;
      }

      const resourceId = item.link.split("/resource/")[1];
      const ownedPass = resourceId ? passOwnershipByResource[resourceId] : null;
      if (!ownedPass) return item;

      return {
        ...item,
        link: `/student/passes/${ownedPass.offerId}`,
      };
    });

    const inbox: InboxMessageItem[] = (((adminMessages as AdminMessageRow[] | null) || []).map((message) => ({
        id: message.id,
        source: "admin_message" as const,
        sender_id: message.sender_id,
        recipient_id: message.recipient_id,
        recipient_filter: message.recipient_filter,
        created_at: message.created_at,
        title: message.title,
        message: message.body,
        link: message.link,
        type: "admin_message" as const,
        priority: message.priority,
        is_read: receiptMap.get(message.id) ?? false,
      })))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 20);

    setNotifications(normalizedNotifications);
    setInboxMessages(inbox);
    setUnreadCount(normalizedNotifications.filter((item) => !item.is_read).length);
    setInboxUnreadCount(inbox.filter((item) => !item.is_read).length);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setInboxMessages([]);
      setUnreadCount(0);
      setInboxUnreadCount(0);
      setLoading(false);
      return;
    }

    void loadNotifications();

    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` }, () => {
        void loadNotifications();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "admin_messages" }, () => {
        void loadNotifications();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "admin_message_receipts", filter: `user_id=eq.${user.id}` }, () => {
        void loadNotifications();
      })
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [loadNotifications, user]);

  const markAsRead = async (notificationId: string) => {
    if (!user) return;

    const target = notifications.find((item) => item.id === notificationId);
    if (!target || target.is_read) return;

    setNotifications((current) =>
      current.map((item) => (item.id === notificationId ? { ...item, is_read: true } : item)),
    );
    setUnreadCount((count) => Math.max(0, count - 1));
    await supabase
      .from("notifications" as never)
      .update({ is_read: true })
      .eq("id", notificationId)
      .eq("user_id", user.id);
    if (target.type === "toofi_nudge") {
      await supabase
        .from("nudge_events" as any)
        .update({ opened_at: new Date().toISOString() } as any)
        .eq("notification_id", notificationId);
    }
  };

  const markAllAsRead = async () => {
    if (!user || unreadCount === 0) return;

    setNotifications((current) => current.map((item) => ({ ...item, is_read: true })));
    setUnreadCount(0);

    await supabase
      .from("notifications" as never)
      .update({ is_read: true })
      .eq("user_id", user.id)
      .eq("is_read", false);
  };

  const markInboxMessageAsRead = async (messageId: string) => {
    if (!user) return;

    const target = inboxMessages.find((item) => item.id === messageId);
    if (!target || target.is_read) return;

    setInboxMessages((current) =>
      current.map((item) => (item.id === messageId ? { ...item, is_read: true } : item)),
    );
    setInboxUnreadCount((count) => Math.max(0, count - 1));

    await supabase
      .from("admin_message_receipts" as never)
      .upsert({
        message_id: messageId,
        user_id: user.id,
        is_read: true,
        read_at: new Date().toISOString(),
      } as never, { onConflict: "message_id,user_id" });
  };

  const markAllInboxAsRead = async () => {
    if (!user || inboxUnreadCount === 0) return;

    const unreadInboxMessages = inboxMessages.filter((item) => !item.is_read);

    setInboxMessages((current) => current.map((item) => ({ ...item, is_read: true })));
    setInboxUnreadCount(0);

    if (unreadInboxMessages.length === 0) return;

    await supabase.from("admin_message_receipts" as never).upsert(
      unreadInboxMessages.map((message) => ({
        message_id: message.id,
        user_id: user.id,
        is_read: true,
        read_at: new Date().toISOString(),
      })) as never,
      { onConflict: "message_id,user_id" },
    );
  };

  const notificationItems: NotificationListItemViewModel[] = notifications.map((item) => ({
    id: item.id,
    iconKind: mapNotificationIconKind(item),
    accentTone: mapNotificationAccentTone(item),
    title: item.title,
    body: item.message,
    explanation: item.explanation,
    timestampLabel: formatNotificationTimestamp(item.created_at),
    destinationLabel: item.destinationLabel,
    badgeLabel: item.badgeLabel,
    isUnread: !item.is_read,
    link: item.link,
  }));

  const inboxItems: InboxListItemViewModel[] = inboxMessages.map((item) => ({
    id: item.id,
    iconKind: "inbox",
    accentTone: item.priority === "urgent" ? "amber" : "blue",
    title: item.title,
    body: item.message,
    timestampLabel: formatNotificationTimestamp(item.created_at),
    badgeLabel: "Admin",
    isUnread: !item.is_read,
    link: item.link,
    priority: item.priority,
  }));

  return {
    notifications,
    inboxMessages,
    notificationItems,
    inboxItems,
    unreadCount,
    inboxUnreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    getNotificationById: (notificationId: string) => notifications.find((item) => item.id === notificationId) ?? null,
    snoozeToofiNudge: async (notificationId: string, untilIso: string) => {
      if (!user) return;
      const result = await supabase
        .from("nudge_events" as any)
        .update({ snoozed_until: untilIso } as any)
        .eq("notification_id", notificationId);
      if (result.error && !hasMissingColumnError(result.error, "snoozed_until")) {
        throw result.error;
      }
      await loadNotifications();
    },
    markInboxMessageAsRead,
    markAllInboxAsRead,
  };
}
